import _, { omit } from 'lodash'

import { AdUnitSizeDTO } from 'features/globalSetup/features/viewport'
import { adUnitSizeToDTO } from 'features/globalSetup/features/viewport/utils'
import { Ability } from 'providers/casl'
import { fieldsToOmit } from 'providers/tanstack-query/utils'
import { EditSchema } from '../../../schemas/editSchema'
import { MediaTypeViewportsSchema } from '../../../schemas/mediaTypesSchema'
import { MediaTypeAttributeDTO, MediaTypesDTOVariable, Variables } from './types'

export const mapper = (data: EditSchema, ability: Ability): Variables => {
  const { divId, id, mediaTypes, name, outOfPage, outOfPageType, pageIds, ybAdUnitEnabled } = data

  const constantFields: Pick<
    Variables,
    'divId' | 'id' | 'mediaTypes' | 'name' | 'outOfPage' | 'outOfPageType' | 'pageIds'
  > = {
    divId: divId || null,
    id,
    mediaTypes: mediaTypesToDTO(mediaTypes),
    name: name || null,
    outOfPage,
    outOfPageType,
    pageIds,
  }

  const variableFields: Pick<Variables, 'ybAdUnitEnabled'> = {
    ybAdUnitEnabled: ybAdUnitEnabled === undefined ? undefined : ybAdUnitEnabled,
  }

  return {
    ...constantFields,
    ...omit(
      variableFields,
      fieldsToOmit<Variables>({
        ybAdUnitEnabled: ability.cannot('update', 'AdUnitField-ybAdUnitEnabled'),
      }),
    ),
  }
}

export const mediaTypesToDTO = ({
  defaultAttribute,
  kind,
  nativeConfiguration,
  videoConfiguration,
  videoType,
  viewportAttributes,
}: EditSchema['mediaTypes']) => ({
  defaultAttribute: kind === 'default' ? defaultAttributeToDTO(defaultAttribute) : undefined,
  ignoreViewportAttributes: kind === 'default' ? true : false,
  nativeConfiguration: JSON.parse(nativeConfiguration),
  videoConfiguration: JSON.parse(videoConfiguration),
  videoType,
  viewportAttributes:
    kind === 'viewports' ? viewportAttributesToDTO(viewportAttributes) : undefined,
})

const defaultAttributeToDTO = ({
  bannerEnabled,
  bannerSizes,
  mediaTypeId,
  nativeEnabled,
  playerSize,
  videoEnabled,
  viewportId,
}: EditSchema['mediaTypes']['defaultAttribute']): MediaTypeAttributeDTO => ({
  bannerEnabled,
  bannerSizes: bannerSizes.map((size: string): AdUnitSizeDTO => adUnitSizeToDTO(size)),
  id: mediaTypeId,
  nativeEnabled,
  playerSize: playerSize.map((size: string): AdUnitSizeDTO => adUnitSizeToDTO(size)),
  videoEnabled,
  viewportId,
})

export const viewportAttributesToDTO = (
  viewportAttributes: EditSchema['mediaTypes']['viewportAttributes'],
): MediaTypesDTOVariable['viewportAttributes'] =>
  viewportAttributes.map(
    (viewportAttribute: MediaTypeViewportsSchema): MediaTypeAttributeDTO =>
      defaultAttributeToDTO(
        _.pick(viewportAttribute, [
          'bannerEnabled',
          'bannerSizes',
          'mediaTypeId',
          'nativeEnabled',
          'videoEnabled',
          'playerSize',
          'viewportId',
          'updatedAt',
        ]),
      ),
  )
